import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ListOrdered, Pin, PinOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/utils/cn";
import type { IChatMessage } from "@/types/(my-space)/chat.interface";
import { messageContentPreview } from "../_lib/chat-labels";
import { nextPinIndex } from "../_lib/pinned";
import { PinnedList } from "./PinnedList";

interface PinnedBarProps {
  /** In the bar's order (a new pin first; members can reorder). */
  pins: IChatMessage[];
  /** Whoever may write may unpin and reorder. */
  canUnpin: boolean;
  onShow: (messageId: number) => void;
  onUnpin: (m: IChatMessage) => void;
  /** Every pinned id, in its new order. */
  onReorder: (messageIds: number[]) => void;
}

/**
 * WhatsApp's pinned bar under the header: one pin at a time, in the pins'
 * order. A click shows that message and moves the bar on to the next one; with
 * several, the list button opens all of them to jump to or drag into order.
 */
export const PinnedBar = ({ pins, canUnpin, onShow, onUnpin, onReorder }: PinnedBarProps) => {
  const { t } = useTranslation("chat");
  const [index, setIndex] = useState(0);
  const [listOpen, setListOpen] = useState(false);
  const count = pins.length;
  // The first pin changed (a new pin, a reorder): the bar starts over on it.
  const firstId = pins[0]?.id;
  useEffect(() => setIndex(0), [firstId, count]);

  if (!count) return null;
  const current = pins[Math.min(index, count - 1)];
  const image = current.type === "image" ? current.attachments[0]?.fileUrl : undefined;
  const preview = messageContentPreview(t, {
    type: current.type,
    body: current.body,
    metadata: current.metadata,
    attachmentCount: current.attachments.length,
    isDeleted: current.isDeleted,
  });
  const title =
    count > 1
      ? t("pinned.title_of", { index: Math.min(index, count - 1) + 1, count })
      : t("pinned.title");

  return (
    <div className="flex items-center gap-2 border-b border-border bg-background px-3 py-1.5 md:px-5">
      <button
        type="button"
        onClick={() => {
          onShow(current.id);
          setIndex((i) => nextPinIndex(i, count));
        }}
        aria-label={`${title}: ${preview}`}
        title={t("pinned.show")}
        className="flex min-w-0 flex-1 items-center gap-3 rounded-md py-0.5 text-start"
      >
        {/* Which of the pins this is, top = latest. */}
        <span aria-hidden className="flex h-8 w-0.5 shrink-0 flex-col gap-0.5">
          {pins.map((p, i) => (
            <span
              key={p.id}
              className={cn(
                "min-h-0 flex-1 rounded-full",
                i === Math.min(index, count - 1) ? "bg-primary" : "bg-border",
              )}
            />
          ))}
        </span>
        {image ? (
          <img src={image} alt="" className="size-8 shrink-0 rounded object-cover" />
        ) : null}
        <span className="flex min-w-0 flex-col">
          <span className="flex items-center gap-1 text-xs font-medium text-primary">
            <Pin className="size-3 shrink-0" />
            {title}
          </span>
          <span dir="auto" className="truncate text-sm text-foreground">
            {preview}
          </span>
        </span>
      </button>
      {count > 1 ? (
        <Popover open={listOpen} onOpenChange={setListOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={t("pinned.list_title")}
              title={t("pinned.list_title")}
            >
              <ListOrdered className="size-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-80 p-0">
            <PinnedList
              pins={pins}
              canEdit={canUnpin}
              onShow={(id) => {
                setListOpen(false);
                onShow(id);
              }}
              onUnpin={onUnpin}
              onReorder={onReorder}
            />
          </PopoverContent>
        </Popover>
      ) : null}
      {canUnpin ? (
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={() => onUnpin(current)}
          aria-label={t("pinned.unpin")}
          title={t("pinned.unpin")}
        >
          <PinOff className="size-4" />
        </Button>
      ) : null}
    </div>
  );
};
